'use client';

import Backdrop from '@mui/material/Backdrop';
import { clsx } from 'clsx';
import { FC, Ref, useEffect, useMemo, useRef } from 'react';
import { Icon, IconType } from 'atoms/Icon';
import { CTA } from 'atoms/Typography/Typography';
import { HeaderState } from 'organisms/Header/Header';
import { SearchIcon } from 'organisms/Header/Icon/SearchIcon';
import { WishlistIcon } from 'organisms/Header/Icon/WishlistIcon';
import { SubMenu } from 'organisms/Header/Navigation/SubMenu';
import { TRANSITION_DURATION } from 'modules/framer-motion/constants';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { usePathname } from 'modules/next/hooks';
import { usePopoverManager } from 'modules/shop/popoverManager/hooks';
import {
  useActiveNavigationItem,
  useSiteConfig,
} from 'modules/shop/siteConfig/hooks';
import { INavigationItem } from 'modules/stroyblok/types';
import { CartIcon } from '../Icon/CartIcon';
import { NAVIGATION_ID } from '../constants';
import { Flyout } from './Flyout';
import { ItemLevel1 } from './ItemLevel1';
import { LoginIcon } from './LoginIcon';
import styles from './Navigation.module.css';

export const Navigation: FC<{
  state: HeaderState;
  ref?: Ref<HTMLDivElement> | null;
  updateState: (s: (s: HeaderState) => HeaderState) => void;
}> = ({ state, ref, updateState }) => {
  const pathname = usePathname();
  const t = useTranslations();
  const { closePopover } = usePopoverManager();
  const { header } = useSiteConfig();
  const { isTablet: isMoreButton } = useMediaQuery();

  const activeItem = useActiveNavigationItem(header);

  const navigationDetailWrapper = useRef<HTMLDivElement | null>(null);

  const navigationMoreItems = useMemo(
    () =>
      header?.children?.filter(
        (navigationItem) =>
          navigationItem.showOnTablet !== true &&
          !navigationItem.disableForLocale,
      ) || [],
    [header, isMoreButton],
  );

  const isBackdropOpen = Boolean(state.visibleItem) || state.showMore;

  const handleToggleShowMore = () => {
    updateState((s: HeaderState) => ({
      ...s,
      showMore: !s.showMore,
      visibleItem: null,
    }));
    closePopover();
  };

  const handleCloseShowMore = () => {
    updateState((s) => ({ ...s, showMore: false }));
  };

  const backdropOnClose = () => {
    updateState((s) => ({ ...s, showMore: false, visibleItem: null }));
  };

  useEffect(() => {
    const navOffsetY: number =
      (
        navigationDetailWrapper.current as HTMLDivElement
      )?.getBoundingClientRect()?.top || 0;

    updateState((s) => ({
      ...s,
      navDetailHeight: window.innerHeight - navOffsetY,
    }));
  }, [state.visibleItem]);

  useEffect(() => {
    updateState((s) => ({ ...s, showMore: false, visibleItem: null }));
  }, [pathname]);

  return (
    <>
      <div
        ref={ref}
        className={clsx(styles.root, state.isSticky && styles.sticky)}
      >
        <nav className={styles.nav} id={NAVIGATION_ID}>
          <ul className={styles.navLinks}>
            {header?.children
              ?.filter((c) => !c.disableForLocale)
              .map((item: INavigationItem) => {
                const isOpen = state.visibleItem?._uid === item._uid;

                return (
                  <li
                    key={item._uid}
                    className={clsx(
                      styles.navLink,
                      !item.showOnTablet && styles.hideOnTablet,
                    )}
                    onClick={handleCloseShowMore}
                  >
                    <ItemLevel1
                      item={item}
                      isOpen={isOpen}
                      setVisibleItem={() => {
                        closePopover();
                        updateState((s) => ({
                          ...s,
                          visibleItem: isOpen ? null : item,
                        }));
                      }}
                    />
                  </li>
                );
              })}
            {isMoreButton && navigationMoreItems && (
              <li
                className={clsx(
                  styles.navLink,
                  state.showMore && styles.active,
                  styles.wrapperMoreButton,
                )}
              >
                <button
                  className={styles.moreButton}
                  onClick={handleToggleShowMore}
                >
                  <CTA type="md">{t('header.buttons.more')} </CTA>
                  <Icon
                    type={IconType.arrowDown}
                    className={clsx(
                      styles.moreIcon,
                      state.showMore && styles.rotate,
                    )}
                  />
                </button>
              </li>
            )}
          </ul>
          {state.isSticky && (
            <div className={styles.rightIcons}>
              <SearchIcon color="white" />
              <WishlistIcon color="white" />
              <LoginIcon
                color="white"
                onClick={() => {
                  backdropOnClose();
                }}
              />
              <CartIcon
                color="white"
                onClick={() => {
                  backdropOnClose();
                }}
              />
            </div>
          )}
        </nav>
        <div ref={navigationDetailWrapper} className={styles.wrapper}>
          {(!!state.visibleItem?.flyoutColumns?.length || state.showMore) && (
            <div className={styles.wrapperInner}>
              {state.showMore && (
                <SubMenu
                  navigationItems={navigationMoreItems}
                  setVisibleItem={(visibleItem: INavigationItem | null) => {
                    updateState((s) => ({ ...s, visibleItem }));
                  }}
                  visibleItem={state.visibleItem}
                  activeItem={activeItem}
                />
              )}
              <Flyout item={state.visibleItem} height={state.navDetailHeight} />
            </div>
          )}
        </div>
      </div>
      {isBackdropOpen && (
        <Backdrop
          open={isBackdropOpen}
          classes={{ root: styles.backdrop }}
          onClick={backdropOnClose}
          transitionDuration={TRANSITION_DURATION}
        />
      )}
    </>
  );
};
